import { TellescopeModelFromName } from "@tellescope/tanstack-query";
import { addHours } from "date-fns/addHours";
import { addMinutes } from "date-fns/addMinutes";
import { startOfDay } from "date-fns/startOfDay";
import { formatInTimeZone, fromZonedTime } from "date-fns-tz";
import { isSameDay } from "date-fns/isSameDay";

export interface EventsByDate<
  T extends TellescopeModelFromName<"calendar_events"> = TellescopeModelFromName<"calendar_events">,
> {
  [date: number]: T[];
}

export const getZonedDayStart = (date: Date, timezone: string) =>
  // Only used internally
  // eslint-disable-next-line @tellescope/require-date-fns-locale
  fromZonedTime(`${formatInTimeZone(date, timezone, "yyyy-MM-dd")} 00:00:00`, timezone);

export const getEventsByDate = <T extends TellescopeModelFromName<"calendar_events">>(
  events: T[],
  timezone?: string,
  limitToStartDate?: boolean
) => {
  const dayStart = (date: Date) => (timezone ? getZonedDayStart(date, timezone) : startOfDay(date));
  const nextDayStart = (date: Date) => dayStart(addHours(date, 25));

  const grouped =
    events?.reduce<EventsByDate<T>>((acc, curr) => {
      const eventStartDate = new Date(curr.startTimeInMS);
      const eventEndDate = addMinutes(eventStartDate, curr.actualDuration ?? curr.durationInMinutes);
      const eventEndMs = eventEndDate.getTime();

      let dateForTraversal = dayStart(eventStartDate);
      while (
        dateForTraversal.getTime() < eventEndMs &&
        (limitToStartDate ? isSameDay(eventStartDate, dateForTraversal) : true)
      ) {
        const timestamp = dateForTraversal.getTime();
        const existingEntry = acc[timestamp] ?? [];
        acc[timestamp] = [...existingEntry, curr];
        dateForTraversal = nextDayStart(dateForTraversal);
      }

      return acc;
    }, {}) ?? {};

  Object.keys(grouped).forEach((timestampKey) => {
    const timestamp = Number(timestampKey);
    const dayEndTimestamp = nextDayStart(new Date(timestamp)).getTime() - 1;
    grouped[timestamp]?.sort((a, b) => {
      const aSpans =
        a.startTimeInMS < timestamp ||
        addMinutes(a.startTimeInMS, a.actualDuration ?? a.durationInMinutes).getTime() > dayEndTimestamp;
      const bSpans =
        b.startTimeInMS < timestamp ||
        addMinutes(b.startTimeInMS, b.actualDuration ?? b.durationInMinutes).getTime() > dayEndTimestamp;
      if (aSpans !== bSpans) return aSpans ? -1 : 1;
      return a.startTimeInMS - b.startTimeInMS;
    });
  });

  return grouped;
};
